import { useInvoicingCompanies } from '@/models/invoicingCompanies';
import { JURISDICTION_LIST } from '@/lib/constants';
import {
  Badge,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Title,
} from '@mantine/core';
import { useMemo } from 'react';
import { useLocation } from 'wouter';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { useUrlState } from '@/hooks/useUrlState';
import { urlField } from '@/lib/urlState';

const INVOICING_COMPANIES_SCHEMA = {
  country: urlField.string(),
  active: urlField.enum(['', 'active', 'inactive'] as const, ''),
};

export default function InvoicingCompaniesList() {
  const [, nav] = useLocation();
  const { data: companies = [], isLoading } = useInvoicingCompanies();
  const [state, setState, { clear, isFiltered }] = useUrlState(
    INVOICING_COMPANIES_SCHEMA,
  );
  const countryFilter = state.country;
  const activeFilter = state.active;

  const filtered = useMemo(
    () =>
      companies.filter((c) => {
        if (countryFilter && c.countryCode !== countryFilter) return false;
        if (activeFilter === 'active' && !c.isActive) return false;
        if (activeFilter === 'inactive' && c.isActive) return false;
        return true;
      }),
    [companies, countryFilter, activeFilter],
  );

  if (isLoading) return <Loader />;

  const jurisdictionName = (code: string) =>
    JURISDICTION_LIST.find((j) => j.code === code)?.title ?? code;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>Payer Companies</Title>
        <Button onClick={() => nav('/new')}>New Company</Button>
      </Group>

      <Group>
        <Select
          placeholder="All countries"
          data={[...new Set(companies.map((c) => c.countryCode))].map((c) => ({
            value: c,
            label: jurisdictionName(c),
          }))}
          value={countryFilter || null}
          onChange={(v) => setState({ country: v ?? '' })}
          clearable
          w={200}
        />
        <Select
          placeholder="Any status"
          data={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
          value={activeFilter || null}
          onChange={(v) =>
            setState({ active: (v ?? '') as typeof state.active })
          }
          clearable
          w={160}
        />
        <ClearFiltersButton visible={isFiltered} onClick={clear} />
      </Group>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Country</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Priority</Table.Th>
            <Table.Th>VAT rows</Table.Th>
            <Table.Th>Banks</Table.Th>
            <Table.Th>Wallets</Table.Th>
            <Table.Th>Invoices</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filtered.map((c) => (
            <Table.Tr
              key={c.id}
              style={{ cursor: 'pointer' }}
              onClick={() => nav(`/${c.id}`)}
            >
              <Table.Td>{c.name}</Table.Td>
              <Table.Td>{jurisdictionName(c.countryCode)}</Table.Td>
              <Table.Td>
                <Badge color={c.isActive ? 'green' : 'gray'}>
                  {c.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Table.Td>
              <Table.Td>{c.priority}</Table.Td>
              <Table.Td>{c.vatRateCount}</Table.Td>
              <Table.Td>{c.bankAccountCount}</Table.Td>
              <Table.Td>{c.cryptoWalletCount}</Table.Td>
              <Table.Td>{c.invoiceCount}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
